import { apiPost } from '../../lib/api';

/**
 * Upload a single File via the presigned POST flow.
 *
 *   1. POST /uploads/presign  -> {url, fields, storage_bucket, storage_key, ...}
 *   2. multipart POST to S3 with the file as the last field
 *   3. POST /uploads/register -> persists a `files` row, returns {download_url}
 *
 * Caller passes optional inspection_id / project_id / deliverable_id and gets
 * back the registered file (with a presigned download URL).
 */
export async function presignAndUpload(file, opts = {}) {
  const presign = await apiPost('/uploads/presign', {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    file_type: opts.file_type || 'raw',
    inspection_id: opts.inspection_id,
    project_id: opts.project_id,
    deliverable_id: opts.deliverable_id,
  });

  // Upload to S3
  const fd = new FormData();
  Object.entries(presign.fields || {}).forEach(([k, v]) => fd.append(k, v));
  fd.append('file', file);
  const r = await fetch(presign.url, { method: 'POST', body: fd });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`S3 upload failed (${r.status}): ${body.slice(0, 200)}`);
  }

  // Register file metadata in the DB
  return apiPost('/uploads/register', {
    storage_bucket: presign.storage_bucket,
    storage_key: presign.storage_key,
    file_type: opts.file_type || 'raw',
    mime_type: file.type || null,
    original_filename: file.name,
    size_bytes: file.size,
    inspection_id: opts.inspection_id,
    project_id: opts.project_id,
    deliverable_id: opts.deliverable_id,
  });
}


/* -----------------------------------------------------------------------
 * Annotator Upload — writes to the canonical S3 inspection layout:
 *   {client}/{industry}/{project}/{deliverable}/{DDMMYYYY}/
 *     images/{filename}    images
 *     report.pdf           PDF report
 *     reports.json         defect details
 *     analytics.json       chart data
 * The browser presigns, uploads directly to S3 for binary, and POSTs JSON
 * payloads to the backend which writes them as object content.
 * ----------------------------------------------------------------------- */
export async function uploadInspectionImage(file, ctx) {
  // ctx: { industry_id, project_id, deliverable_id, task_date }
  const presign = await apiPost('/uploads/inspection-asset/presign', {
    industry_id:    ctx.industry_id,
    project_id:     ctx.project_id,
    deliverable_id: ctx.deliverable_id,
    task_date:      ctx.task_date,
    asset:          'image',
    filename:       file.name,
    content_type:   file.type || 'application/octet-stream',
    size_bytes:     file.size,
  });
  const fd = new FormData();
  Object.entries(presign.fields || {}).forEach(([k, v]) => fd.append(k, v));
  fd.append('file', file);
  const r = await fetch(presign.url, { method: 'POST', body: fd });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`Image upload failed (${r.status}): ${body.slice(0, 200)}`);
  }
  return presign; // contains storage_key, prefix, timestamp
}

export async function uploadInspectionReportPdf(file, ctx) {
  const presign = await apiPost('/uploads/inspection-asset/presign', {
    industry_id:    ctx.industry_id,
    project_id:     ctx.project_id,
    deliverable_id: ctx.deliverable_id,
    task_date:      ctx.task_date,
    asset:          'report',
    filename:       file.name || 'report.pdf',
    content_type:   file.type || 'application/pdf',
    size_bytes:     file.size,
  });
  const fd = new FormData();
  Object.entries(presign.fields || {}).forEach(([k, v]) => fd.append(k, v));
  fd.append('file', file);
  const r = await fetch(presign.url, { method: 'POST', body: fd });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`Report PDF upload failed (${r.status}): ${body.slice(0, 200)}`);
  }
  return presign;
}

export async function writeInspectionSidecar(asset, payload, ctx) {
  // asset: "reports_json" | "analytics_json"
  return apiPost('/uploads/inspection-asset/sidecar', {
    industry_id:    ctx.industry_id,
    project_id:     ctx.project_id,
    deliverable_id: ctx.deliverable_id,
    task_date:      ctx.task_date,
    asset,
    payload,
  });
}
